import {Component, Inject, LOCALE_ID, OnInit, Renderer2} from '@angular/core';
import {ConfigService} from '../@config/config/config.service';
import {Settings} from 'luxon';
import {DOCUMENT} from '@angular/common';
import {Platform} from '@angular/cdk/platform';
import {NavigationService} from '../@config/services/navigation.service';
import {LayoutService} from '../@config/services/layout.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {SplashScreenService} from '../@config/services/splash-screen.service';
import {AppConfigName} from '../@config/config/config-name.model';
import {ColorSchemeName} from '../@config/config/colorSchemeName';
import {MatIconRegistry, SafeResourceUrlWithIconOptions} from '@angular/material/icon';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {ColorVariable, colorVariables} from '../@config/components/config-panel/color-variables';
import {TranslateService} from '@ngx-translate/core';
import {secureStorage} from './shared/functions/secure-storage';
import {AuthService} from "./services/auth.service";
import {SideNavItems} from "../@config/sidenav/sidenav-items";
import {GetLanguage, GetPreferredDate, setMomentLocal} from "./shared/functions/shared-functions";
import {filter, map, mergeMap} from "rxjs/operators";
import {PageTitleService} from "./core/components/page-title/page-title.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {CoreService} from "./services/core.service";
import moment from "moment";
import {GlobalService} from "./services/global.service";
import {BreadcrumbsService} from "@exalif/ngx-breadcrumbs";

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  constructor(
    private translate: TranslateService,
    private configService: ConfigService,
    private renderer: Renderer2,
    private platform: Platform,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private router: Router,
    private coreService: CoreService,
    private pageTitleService: PageTitleService,
    private navigationService: NavigationService,
    private splashScreenService: SplashScreenService,
    private authService: AuthService,
    private sideNavItems: SideNavItems,
    private globalService: GlobalService,
    private breadcrumbsService: BreadcrumbsService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer) {
  }

  ngOnInit() {
    this.setTitle();
    setMomentLocal();
    Settings.defaultLocale = this.localeId;
    // GetDeviceInfoFromIPAddress();
    // this.globalService.getMyCountryByIp();
    // this.getCountry();
    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }
    this.authService.isAuthorized() && this.configService.updateConfig({
      toolbar: {
        user: {
          visible: true,
        },
      },
    });
    this.registerIcons();

    /**
     * Customize the template to your needs with the ConfigService
     * Example:
     *  this.configService.updateConfig({
     *    sidenav: {
     *      title: 'Custom App',
     *      imageUrl: '//placehold.it/100x100',
     *      showCollapsePin: false
     *    },
     *    footer: {
     *      visible: false
     *    }
     *  });
     */

    /**
     * Config Related Subscriptions
     * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
     * Example: example.com/?layout=apollo&style=default
     */
    this.route.queryParamMap.subscribe(queryParamMap => {
      if (queryParamMap.has('layout')) {
        this.configService.setConfig(queryParamMap.get('layout') as AppConfigName);
      }

      if (queryParamMap.has('style')) {
        this.configService.updateConfig({
          style: {
            colorScheme: queryParamMap.get('style') as ColorSchemeName
          }
        });
      }

      if (queryParamMap.has('primaryColor')) {
        const color: ColorVariable = colorVariables[queryParamMap.get('primaryColor')];

        if (color) {
          this.configService.updateConfig({
            style: {
              colors: {
                primary: color
              }
            }
          });
        }
      }

      if (queryParamMap.has('rtl')) {
        this.configService.updateConfig({
          direction: coerceBooleanProperty(queryParamMap.get('rtl')) ? 'rtl' : 'ltr'
        });
      }
    });

    /**
     * Add your own routes here
     */
    this.navigationService.items = this.sideNavItems.getAll()
    this.setLocalization();
    // this.getQuickList();
  }

  getQuickList() {
    this.coreService.getRequest(`admin/quick-list`).pipe(untilDestroyed(this)).subscribe(
      res => {
        this.configService.quickList$.next(res?.data);
      }
    )
  }

  getCountry() {
    this.coreService.getRequest('get-user-country-info').pipe(untilDestroyed(this)).subscribe(res => {
      secureStorage.setItem('location', {
        country: res?.country?.replace(' ', ''),
      })
    })
  }

  registerIcons() {
    this.matIconRegistry.addSvgIconResolver(
      (
        name: string,
        namespace: string
      ): SafeResourceUrl | SafeResourceUrlWithIconOptions | null => {
        switch (namespace) {
          case 'mat':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/material-design-icons/two-tone/${name}.svg`
            );

          case 'logo':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/logos/${name}.svg`
            );

          case 'flag':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/flags/${name}.svg`
            );
        }
      }
    );
  }

  setLocalization() {
    this.translate.addLangs(['en', 'ar']);
    this.translate.setDefaultLang('en');
    const lang = secureStorage.getItem('lang') || 'en';
    secureStorage.setItem('lang', lang);
    this.translate.use(lang);
    this.configService.updateConfig({
      direction: lang == 'en' ? 'ltr' : 'rtl'
    });
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.body.style.direction = dir;
    (window as any).primaryColor = '#045DA9';
    (window as any).secondaryColor = '#F5841F';
    (window as any).greyColor = '#929391';
    document.documentElement.style.setProperty('--primary-color', (window as any).primaryColor);
    document.documentElement.style.setProperty('--secondary-color', (window as any).secondaryColor);
    document.documentElement.style.setProperty('--grey-color', (window as any).greyColor);
  }

  setTitle() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => {
        return route.outlet === 'primary';
      }),
      mergeMap((route) => route.data)
    ).subscribe((event) => {
      this.pageTitleService.setTitle(event['title'])
    });
  }
}
